import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import StatusBadge from "../components/StatusBadge";
import { Plus, ArrowRight, PackageOpen, Clock, Truck, XOctagon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STAT_ITEMS = [
  { key: "Eingetroffen", icon: PackageOpen, color: "#0055FF", testid: "stat-card-eingetroffen" },
  { key: "In Bearbeitung", icon: Clock, color: "#B45309", testid: "stat-card-in-bearbeitung" },
  { key: "Erneut versendet", icon: Truck, color: "#6D28D9", testid: "stat-card-erneut" },
  { key: "Storniert", icon: XOctagon, color: "#B91C1C", testid: "stat-card-storniert" },
  { key: "Abgeschlossen", icon: CheckCircle2, color: "#047857", testid: "stat-card-abgeschlossen" },
];

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/returns/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/returns").then((r) => setRecent(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <p className="label-mini">{t("dashboard.welcome")}, {user?.name}</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-none mt-1">{t("dashboard.title")}</h1>
        </div>
        <Button
          onClick={() => navigate("/returns/new")}
          className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
          style={{ borderRadius: 4 }}
          data-testid="dashboard-new-return-button"
        >
          <Plus className="w-4 h-4 mr-2" /> {t("dashboard.newReturn")}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
        {STAT_ITEMS.map((s, i) => (
          <div
            key={s.key}
            data-testid={s.testid}
            className="swiss-card p-4 fade-up cursor-pointer hover:-translate-y-0.5"
            style={{ animationDelay: `${i * 60}ms` }}
            onClick={() => navigate(`/returns?status=${encodeURIComponent(s.key)}`)}
          >
            <div className="flex items-start justify-between mb-3">
              <s.icon className="w-5 h-5" style={{ color: s.color }} />
              <span className="label-mini">{t(`stats.${s.key}`)}</span>
            </div>
            <div className="text-4xl font-bold tracking-tight leading-none">{stats[s.key] ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Recent */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-bold tracking-tight">{t("dashboard.recent")}</h2>
        <Link to="/returns" className="text-sm text-[#0055FF] hover:underline inline-flex items-center" data-testid="dashboard-view-all">
          {t("dashboard.viewAll")} <ArrowRight className="w-4 h-4 ml-1" />
        </Link>
      </div>

      {recent.length === 0 ? (
        <div className="swiss-card p-8 text-center text-muted-foreground" data-testid="dashboard-empty">
          {t("dashboard.empty")}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dashboard-recent-list">
          {recent.map((r, i) => (
            <Link
              to={`/returns/${r.id}`}
              key={r.id}
              className="swiss-card p-4 fade-up block"
              style={{ animationDelay: `${i * 40}ms` }}
              data-testid={`recent-return-${r.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-xs text-muted-foreground">{r.id}</span>
                <StatusBadge status={r.status} />
              </div>
              <div className="font-semibold truncate">{r.customer_name}</div>
              <div className="text-sm text-muted-foreground truncate">
                {t("returns.cols.order")}: {r.order_number}
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                {r.shop_origin || "—"} ·{" "}
                {r.created_at && new Date(r.created_at).toLocaleDateString(lang === "de" ? "de-DE" : "en-US")}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
